/**
 * Free bottle offer - 2 free bottles for every 6 bottles bought, however they are bought.
 *
 * A "bottle" is 60 capsules, so a 120-capsule pack counts as 2 bottles and a
 * 180-capsule pack as 3. Six 60s, three 120 packs, two 180 packs, or any mix that
 * reaches 6 earns 2 free 60-capsule bottles; 12 earns 4, and so on.
 *
 * Why a $0 gift product instead of a Shopify discount:
 *  - Shopify's Buy X Get Y counts items, not capsules, so it cannot see two
 *    180-capsule packs as six bottles.
 *  - It also cannot combine with other savings on the bottles being paid for, so
 *    "free" bottles added through it could push the cart total UP.
 * A gift product priced at $0 is free by price, needs no discount at all.
 *
 * The gift line is kept equal to what the cart has earned: added when the cart
 * reaches 6 bottles, reduced or removed when it drops back, so a gift can never be
 * kept without the qualifying bottles in the cart.
 *
 * When free bottles are newly earned it dispatches `free-bottle-offer:unlocked`, which
 * assets/free-bottle-celebration.js turns into the congratulations popup.
 *
 * If Shopify refuses to add the gift (gift product unavailable, or cart requests being
 * rate-limited), the script stops trying for a few minutes instead of sending another
 * failing request on every cart change - extra cart requests make Shopify's rate limit,
 * which silently drops the customer's own add-to-cart, more likely.
 *
 * Settings come from window.FreeBottleOffer, output by snippets/free-bottle-offer.liquid.
 */
(() => {
  const config = window.FreeBottleOffer;
  if (!config) return;

  const EVENT_SOURCE = 'free-bottle-offer';
  const CELEBRATED_KEY = 'free-bottle-offer:celebrated';
  const GIFT_PAUSED_UNTIL_KEY = 'free-bottle-offer:gift-paused-until';
  const GIFT_RETRY_AFTER_MS = 5 * 60 * 1000;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  let isSyncing = false;
  let syncQueued = false;

  const request = (path, body) =>
    fetch(`${root}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    }).then((response) => {
      if (!response.ok) {
        const error = new Error(`${path} responded ${response.status}`);
        error.status = response.status;
        throw error;
      }
      return response.json();
    });

  const storage = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, String(value));
      } catch (error) {
        // Storage unavailable (private mode, blocked site data): behaviour degrades gracefully.
      }
    },
  };

  // The number of free bottles already celebrated this session, so the popup only appears
  // when free bottles are newly earned, not on every page load or cart change.
  const readCelebrated = () => Number(storage.get(CELEBRATED_KEY)) || 0;
  const writeCelebrated = (value) => storage.set(CELEBRATED_KEY, value);

  const giftAddsPaused = () => Number(storage.get(GIFT_PAUSED_UNTIL_KEY)) > Date.now();
  const pauseGiftAdds = () => storage.set(GIFT_PAUSED_UNTIL_KEY, Date.now() + GIFT_RETRY_AFTER_MS);

  const isGiftLine = (item) => item.variant_id === config.giftVariantId;

  // Bottles bought, in 60-capsule bottles. Gift lines are never counted (they are not
  // in bottlesPerVariant), and subscription lines only count if the offer includes them.
  const bottlesInCart = (cart) =>
    cart.items.reduce((total, item) => {
      if (item.selling_plan_allocation && !config.includeSubscriptions) return total;
      const bottlesPerUnit = config.bottlesPerVariant[item.variant_id] || 0;
      return total + bottlesPerUnit * item.quantity;
    }, 0);

  // Re-render the cart drawer / cart page and the header count using the theme's own event.
  const refreshCart = (cart) => {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: EVENT_SOURCE,
          data: { source: EVENT_SOURCE, itemCount: cart.item_count },
        },
      })
    );
  };

  const sync = async () => {
    if (isSyncing) {
      syncQueued = true;
      return;
    }
    isSyncing = true;

    try {
      const cart = await request('cart.js');
      const earned = Math.floor(bottlesInCart(cart) / config.bottlesRequired) * config.freeBottles;
      const giftLines = cart.items.filter(isGiftLine);
      const giftQuantity = giftLines.reduce((total, item) => total + item.quantity, 0);

      if (giftQuantity === earned) {
        // Nothing to change. Recording the current level means a page load with the gift
        // already in the cart doesn't celebrate, and dropping below a level lets it be
        // celebrated again when it's re-earned.
        writeCelebrated(earned);
        return;
      }

      // Adding gifts is paused after a refusal. Removing gifts (earned < giftQuantity) is
      // always allowed, so a customer is never left holding free bottles they no longer earn.
      if (earned > giftQuantity && giftAddsPaused()) return;

      const celebrated = readCelebrated();

      if (giftLines.length === 0) {
        await request('cart/add.js', { items: [{ id: config.giftVariantId, quantity: earned }] });
      } else {
        // Keep a single gift line: clear any extras, then set the first to what was earned.
        for (const extra of giftLines.slice(1)) {
          await request('cart/change.js', { id: extra.key, quantity: 0 });
        }
        await request('cart/change.js', { id: giftLines[0].key, quantity: earned });
      }

      refreshCart(await request('cart.js'));

      if (earned > celebrated) {
        document.dispatchEvent(new CustomEvent('free-bottle-offer:unlocked', { detail: { freeBottles: earned } }));
      }
      writeCelebrated(earned);
    } catch (error) {
      // 404 / 422: the gift variant can't be sold (e.g. product set to Draft).
      // 429: Shopify is rate-limiting cart requests. Either way, stop adding for a while.
      if (error.status === 404 || error.status === 422 || error.status === 429) pauseGiftAdds();
      console.error('[free-bottle-offer]', error);
    } finally {
      isSyncing = false;
      if (syncQueued) {
        syncQueued = false;
        sync();
      }
    }
  };

  // `cart:update` is the theme's event (product form, cart drawer quantity changes).
  // `cart:updated` is fired by the custom .ajax-add-to-cart buttons in layout/theme.liquid.
  const onCartChanged = (event) => {
    if (event.detail && event.detail.data && event.detail.data.source === EVENT_SOURCE) return;
    setTimeout(sync, 0);
  };
  document.addEventListener('cart:update', onCartChanged);
  document.addEventListener('cart:updated', onCartChanged);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }
})();
